import * as React from 'react'
import { useIntl, useLocation } from 'umi'
import { inject } from 'mobx-react'
import { common } from '@/models' // 导入 common 模型
import { Icon, Tooltip } from '@/components/index'
import { durationTime } from '@/utils/common'
import RTCModel, { NetworkStatus } from '@/models/rtc'
import { ROLE } from '@/types/meeting'
import UserModel from '@/models/user'
import Meeting from '@/models/meeting'
import s from './index.less'
import { TranscribeStatus } from '@/pages/Meeting/component/ControlBar/PC/TranscribeBtn'
import { focused, transcribe } from '@/models'
import ChangeModeBtn from '@/pages/Meeting/component/ChangeModeBtn'
import { MODE } from '@/models/meeting'
import classNames from 'classnames'

interface HeaderProps {
  operationRender?: () => React.ReactNode
  createdTime?: number
  RTCInstance?: RTCModel
  meeting?: Meeting
  user?: UserModel
}

export const Header: React.FC<HeaderProps> = inject(({ store }) => {
  return {
    RTCInstance: store.rtc,
    user: store.user,
    meeting: store.meeting,
  }
})(props => {
  const { RTCInstance, user, operationRender, createdTime = 0, meeting } = props
  const { formatMessage } = useIntl()
  const [now, setNow] = React.useState(new Date().getTime())
  const location = useLocation()
  const [netWorkStatus, setNetWorkStatus] = React.useState<NetworkStatus>(NetworkStatus.UNKNOWN)

  React.useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date().getTime())
    }, 1000)
    if (!location.pathname.includes('meeting')) {
      clearInterval(timer)
    }
    return () => {
      clearInterval(timer)
    }
  }, [location])

  React.useEffect(() => {
    const localNetWorkStatus = RTCInstance?.networkQualityMap.get(user?.yxuid!)
    const downlinkNetworkQuality = Number(localNetWorkStatus?.downlinkNetworkQuality!)
    const uplinkNetworkQuality = Number(localNetWorkStatus?.uplinkNetworkQuality!)
    const networkValue = Math.max(downlinkNetworkQuality, uplinkNetworkQuality)
    if (networkValue >= NetworkStatus.BAD) {
      setNetWorkStatus(NetworkStatus.BAD)
    } else if (networkValue >= NetworkStatus.POOR) {
      setNetWorkStatus(NetworkStatus.POOR)
    } else if (networkValue >= NetworkStatus.EXCELLENT) {
      setNetWorkStatus(NetworkStatus.EXCELLENT)
    } else {
      setNetWorkStatus(NetworkStatus.EXCELLENT)
    }
  }, [RTCInstance?.networkQualityMap.get(user?.yxuid!), user?.yxuid])

  const time = React.useMemo(() => {
    if (!createdTime) return '00:00'
    return durationTime(now, createdTime)
  }, [createdTime, now])

  return (
    <div className={s.header}>
      <div className={s.left}>
        <div className={s.meeting}>
          <span className={s.info}>{meeting?.info?.meetingName}</span>
          <span className={s.split}>|</span>
          <span className={s.info}>
            {formatMessage({ id: 'CONFERENCE_NUMBER' })}：{meeting?.info?.meetingId || Number(meeting?.info?.meetingId)}
          </span>
          <span className={s.split}>|</span>
          <span className={s.time}>{time}</span>
          <span className={s.split}>|</span>

          <Tooltip
            title={formatMessage({ id: 'internetConnectionTest' })}
            placement="bottom"
            align={{ offset: [0, 10] }}
          >
            <div className={s.netWorkStatus}>
              <Icon
                name="icon-signal_1"
                mode="symbol"
                size={16}
                className={classNames(
                  netWorkStatus === NetworkStatus.BAD ? s.bad : '',
                  netWorkStatus === NetworkStatus.POOR ? s.poor : '',
                  netWorkStatus === NetworkStatus.EXCELLENT ? s.excellent : '',
                )}
              ></Icon>
            </div>
          </Tooltip>

          <div className={s.statusWrapper}>
            {meeting?.screenUid && (
              <div className={s.recording}>
                <Icon
                  className={s.icon}
                  name="icon-a-Mirrorshare"
                  size={16}
                  style={{ color: meeting.videoRecordingState === 1 ? '#FF525D' : '#999999' }}
                />
                <span>
                  {formatMessage({
                    id: 'onShareing',
                  })}
                </span>
              </div>
            )}
            {[1, 2].includes(meeting?.videoRecordingState!) && (
              <div className={s.recording}>
                <Icon
                  className={s.icon}
                  name="icon-luzhi"
                  size={16}
                  style={{ color: meeting?.videoRecordingState! === 1 ? '#FF525D' : '#999999' }}
                />
                <span>
                  {formatMessage({
                    id: meeting?.videoRecordingState === 1 ? 'RECORDING2' : 'RECORDING_PAUSED',
                  })}
                </span>
              </div>
            )}
            {[TranscribeStatus.intranscribe, TranscribeStatus.pauseTranscribe].includes(transcribe.transcribeStatus) &&
              // 屏蔽条件：网易招聘 AND 游客身份
              // 显示条件：不是（网易招聘 AND 游客身份）
              !(common?.isGroupInterview && meeting?.getMemberByYxuid(user?.yxuid!)?.role === ROLE.GUEST) && (
                <div className={s.recording}>
                  <Icon
                    className={s.icon}
                    name="icon-summary"
                    mode="symbol"
                    size={16}
                    style={{
                      color: transcribe.transcribeStatus === TranscribeStatus.intranscribe ? '#FF525D' : '#999999',
                    }}
                  />
                  <span>
                    {formatMessage({
                      id:
                        transcribe.transcribeStatus === TranscribeStatus.intranscribe
                          ? 'inTranscript'
                          : 'transcribePaused',
                    })}
                  </span>
                </div>
              )}
          </div>

          <span className={classNames(s.info, s.isTalking)}>
            {formatMessage({ id: 'isTalking' })}
            {meeting?.speakerName || ''}
          </span>
        </div>
      </div>
      <div className={s.right}>
        {meeting?.memberCount! >= 2 &&
          !RTCInstance?.breakLineReconnecting &&
          !meeting?.whiteboardUid &&
          !meeting?.codeEditorEnabled &&
          !focused?.focusedYuxid && (
            <ChangeModeBtn
              value={meeting?.viewMode!}
              onChange={(value: MODE) => {
                meeting?.setViewMode(value, true)
              }}
            ></ChangeModeBtn>
          )}
        {focused?.focusedYuxid && (
          <Tooltip
            title={
              <div className={s.switchLayout}>
                <Icon name="icon-focus_solid_1" size={32} className={s.icon} mode="symbol"></Icon>
                <span className={s.text}>{formatMessage({ id: 'unableToSwitchLayout' })}</span>
              </div>
            }
            placement="bottom"
            trigger={['click']}
            className={s.lookAtTooltip}
          >
            <div className={s.focusedName}>
              <Tooltip title={formatMessage({ id: 'spotLighted' })} placement="bottomLeft" className={s.lookAtHim}>
                <div className={s.focusedIcon}>
                  <Icon name="icon-focus_solid_1" size={20} mode="symbol" className={s.icon}></Icon>
                </div>
              </Tooltip>
              {meeting?.membersMap.get(focused?.focusedYuxid)?.memberName}
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  )
})
